import { useState, useEffect, useCallback, useMemo } from "react";
import { useAppMode } from "../context/AppMode";
import userService from "../services/userService";
import { isActive, sortUsersByName } from "../utils";

/**
 * Hook pour fournir les cavaliers actifs et gérer le user courant
 * Compatible avec UserSelectorModal
 */
export function useUserHomeSelect() {
  const { currentUser, selectUser } = useAppMode();
  const [activeUsers, setActiveUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // --- Load active users ---
  const loadActiveUsers = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const users = await userService.getAll();
      const actifs = users.filter((r) =>
        isActive(r.activity_start_date, r.activity_end_date),
      );
      setActiveUsers(sortUsersByName(actifs));
    } catch (err) {
      console.error("Erreur lors du chargement des utilisateurs :", err);
      setActiveUsers([]);
      setError(err.message || "Erreur lors du chargement des utilisateurs");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadActiveUsers();
  }, [loadActiveUsers]);

  // --- Clear current user ---
  const clearUser = useCallback(() => {
    selectUser(null);
  }, [selectUser]);

  // --- Select user callback compatible with modal ---
  const handleSelectUser = useCallback(
    (user) => {
      selectUser(user);
    },
    [selectUser],
  );

  // Memoized result for modal consumption
  const users = useMemo(() => activeUsers, [activeUsers]);

  return {
    activeUsers: users,
    loading,
    error,
    user: currentUser,
    selectUser: handleSelectUser,
    clearUser,
    reload: loadActiveUsers,
  };
}
